import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2, AlertTriangle, Ban } from 'lucide-react';
import { api } from '../../core/apiClient';

interface ExclusionKeywordStat {
  keyword: string;
  count: number;
}

/**
 * Estadística de paraules clau d'exclusió (pestanya "Estadística criteris
 * d'eliminació" de Licitacions) — quantes vegades s'ha fet servir cada
 * paraula per descartar automàticament una licitació en la ingesta per
 * email. Dades extretes del camp `observations` de Tender (vegeu
 * tenderService.getExclusionKeywordStats al backend).
 */
export default function TenderExclusionStats() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<ExclusionKeywordStat[]>([]);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const res = await api.get<{ success: boolean; data: ExclusionKeywordStat[] }>('/tenders/stats/exclusion-keywords');
        setStats(res.data ?? []);
      } catch (err) {
        console.error('[TenderExclusionStats] Error carregant estadístiques:', err);
        setError('Error en carregar l\'estadística de criteris d\'eliminació');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const total = stats.reduce((s, r) => s + r.count, 0);
  const maxCount = stats.length > 0 ? stats[0].count : 0;
  const goBack = () => { sessionStorage.setItem('erp:crm:activeTab', JSON.stringify('licitacions')); navigate('/crm'); };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={goBack} className="p-1.5 rounded hover:bg-gray-100">
            <X size={18} className="text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Estadística criteris d'eliminació</h1>
            <p className="text-xs text-muted-foreground">
              Vegades que cada paraula clau d'exclusió ha descartat automàticament una licitació
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        ) : stats.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg px-5 py-10 text-center">
            <Ban className="h-8 w-8 mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              Encara no s'ha descartat cap licitació per paraula clau d'exclusió.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden max-w-2xl">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Ban size={16} className="text-rose-600" /> Paraules clau d'exclusió
              </h2>
              <span className="text-xs text-muted-foreground">{total} descart{total === 1 ? '' : 's'} en total</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-muted-foreground uppercase tracking-wide">
                <tr>
                  <th className="text-left px-5 py-2 font-medium">Paraula clau</th>
                  <th className="text-right px-5 py-2 font-medium w-24">Vegades</th>
                  <th className="px-5 py-2 font-medium w-40"></th>
                </tr>
              </thead>
              <tbody>
                {stats.map((row) => (
                  <tr key={row.keyword} className="border-t border-gray-100">
                    <td className="px-5 py-2 text-gray-900">{row.keyword}</td>
                    <td className="px-5 py-2 text-right font-medium tabular-nums">{row.count}</td>
                    <td className="px-5 py-2">
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-rose-500 rounded-full"
                          style={{ width: `${maxCount > 0 ? (row.count / maxCount) * 100 : 0}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
